import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useGame } from '@/context/GameContext';

export default function LevelUpModal() {
    const { levelUpPending, stats, dismissLevelUp } = useGame();

    useEffect(() => {
        if (!levelUpPending) return;

        const duration = 3 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 999 };

        const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                clearInterval(interval);
                return;
            }

            const particleCount = 50 * (timeLeft / duration);
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
        }, 250);

        const timer = setTimeout(dismissLevelUp, 5000);
        return () => {
            clearInterval(interval);
            clearTimeout(timer);
        };
    }, [dismissLevelUp, levelUpPending]);

    return (
        <AnimatePresence>
            {levelUpPending && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#050505]/84 p-6 backdrop-blur-sm" onClick={dismissLevelUp}>
                    <motion.div
                        initial={{ scale: 0.88, opacity: 0, y: 24 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 1.04, opacity: 0, y: -18 }}
                        className="relative w-full max-w-xl overflow-hidden border border-[#2d3554] bg-[#101010] shadow-[0_0_38px_rgba(140,172,255,0.18)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(140,172,255,0.1),transparent_58%)]" />
                        <div className="relative border-b border-[#26304f] bg-[#171d2f] px-8 py-6">
                            <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.36em] text-[#8cacff]">
                                SYSTEM_ASCENSION
                            </p>
                            <h2 className="mt-4 font-['Space_Grotesk'] text-5xl font-black uppercase italic text-white md:text-6xl">
                                Level
                                <span className="text-[#00cffc]"> Up</span>
                            </h2>
                        </div>

                        <div className="relative px-8 py-8">
                            <div className="grid gap-5 md:grid-cols-[0.9fr_1.1fr]">
                                <div className="border border-[#2f3859] bg-[#131929] p-6 text-center">
                                    <motion.div
                                        animate={{ rotateY: 360 }}
                                        transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 1 }}
                                        className="mx-auto flex h-20 w-20 items-center justify-center border border-[#8cacff] bg-[#18203a] text-4xl text-[#8cacff]"
                                    >
                                        ★
                                    </motion.div>
                                    <p className="mt-5 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#767575]">
                                        Current Level
                                    </p>
                                    <p className="mt-2 font-['Space_Grotesk'] text-5xl font-black uppercase text-white">
                                        {stats?.level}
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#00cffc]">
                                            Upgrade Unlocked
                                        </p>
                                        <p className="mt-3 font-['Space_Grotesk'] text-2xl font-black uppercase italic text-white">
                                            +10% XP Multiplier
                                        </p>
                                    </div>
                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                        <p className="text-sm leading-relaxed text-[#acabaa]">
                                            Your operator profile just synced to a higher clearance band. Keep pushing missions to compound the multiplier.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <button
                                className="mt-8 inline-flex w-full items-center justify-center border border-[#8cacff] bg-[#1a2236] px-6 py-5 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.22em] text-white transition-colors hover:bg-[#222c49]"
                                onClick={dismissLevelUp}
                            >
                                Continue Grind
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
